/**
 * Checks and freezes one finder kind: a kind of search the finder runs, such as providers or events.
 *
 * @param {Object} definition
 * @param {String} definition.name the value of `<sp-finder kind>`
 * @param {Object<String, String>} [definition.results] each result type the server sends, mapped to the
 *   registered element that draws it: `{ Article: "sp-finder-article-result" }`
 * @param {Function} [definition.resultProperties] the properties the result elements take besides `result`,
 *   built from `{ result, payload, settings }` (the finder's results payload and settings)
 * @param {Array<Object>} [definition.listInserts] registered elements the results list draws between its
 *   results: `{ after: 5, element: "sp-finder-map-banner", whenFewer: "end" }` draws the banner after the
 *   fifth result, and at the end of a shorter list. Without `whenFewer`, a shorter list draws no insert.
 * @param {Object<String, Object>} [definition.requests] requests the kind's blocks run through the finder,
 *   each a `url(settings)` that returns the endpoint from the finder's settings; the request owner supplies
 *   the params as its `<name>Params` command
 * @returns {Readonly<Object>} the frozen definition
 */
export function defineFinderKind({ name, results, resultProperties, listInserts = [], requests = {}, ...rest }) {
  if (typeof name !== "string" || name === "") {
    throw new TypeError("A finder kind needs a name");
  }
  if (Object.keys(rest).length > 0) {
    throw new TypeError(`Finder kind "${name}" declares unknown keys: ${Object.keys(rest).join(", ")}`);
  }
  if (results !== undefined) {
    const entries = Object.entries(results ?? {});
    if (entries.length === 0) {
      throw new TypeError(`Finder kind "${name}" declares an empty results map`);
    }
    for (const [type, tag] of entries) {
      assertElement(name, `result type ${type}`, tag);
    }
  }
  if (resultProperties !== undefined && typeof resultProperties !== "function") {
    throw new TypeError(`Finder kind "${name}" resultProperties must be a function`);
  }
  for (const insert of listInserts) {
    assertElement(name, "a list insert", insert.element);
    if (!Number.isInteger(insert.after) || insert.after < 0) {
      throw new TypeError(`Finder kind "${name}" places ${insert.element} after a position that is not a count`);
    }
    if (insert.whenFewer !== undefined && insert.whenFewer !== "end") {
      throw new TypeError(`Finder kind "${name}" gives ${insert.element} an unknown whenFewer "${insert.whenFewer}"`);
    }
  }
  for (const [requestName, request] of Object.entries(requests)) {
    if (typeof request?.url !== "function") {
      throw new TypeError(`Finder kind "${name}" request ${requestName} needs a url(settings) function`);
    }
  }

  return Object.freeze({
    name,
    results: Object.freeze({ ...results }),
    resultProperties: resultProperties ?? noResultProperties,
    listInserts: Object.freeze(listInserts.map((insert) => Object.freeze({ ...insert }))),
    requests: Object.freeze({ ...requests }),
  });
}

function assertElement(name, what, tag) {
  if (typeof tag !== "string" || !tag.startsWith("sp-")) {
    throw new TypeError(`Finder kind "${name}" maps ${what} to "${tag}", which is not an sp-* element`);
  }
}

function noResultProperties() {
  return {};
}
